import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, fmtDate, fmtDateTime, BACKEND } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Inbox, Paperclip, ArrowRightLeft, ExternalLink, Eye, Upload, FileText, CheckCircle2 } from "lucide-react";
import { usePresence, usePresenceBulk } from "@/hooks/usePresence";
import { PresenceIndicator, PresenceChip } from "@/components/PresenceIndicator";
import { toast } from "sonner";

const EST = {
  nuevo: { label: "Nuevo", cls: "bg-blue-100 text-blue-700 border-blue-300" },
  en_proceso: { label: "En proceso", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  convertida: { label: "Convertida", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  descartada: { label: "Descartada", cls: "bg-slate-100 text-slate-500 border-slate-300" },
};

export default function CotizacionesWeb() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("todas");
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(null);
  const [nota, setNota] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const presenceMap = usePresenceBulk("quote_request", [rows.length]);
  const detailViewers = usePresence("quote_request", open && cur ? cur.id : null, open && !!cur);

  const load = async () => {
    try {
      const { data } = await api.get("/quote-requests");
      setRows(data);
      if (cur) { const fresh = data.find((r) => r.id === cur.id); if (fresh) setCur(fresh); }
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const counts = rows.reduce((a, r) => { a[r.estatus] = (a[r.estatus] || 0) + 1; return a; }, {});
  const view = filter === "todas" ? rows : rows.filter((r) => r.estatus === filter);

  const openDetail = (r) => { setCur(r); setNota(""); setOpen(true); };
  const setEstatus = async (estatus) => {
    try { await api.put(`/quote-requests/${cur.id}`, { estatus }); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const submitNote = async () => {
    if (!nota.trim()) return toast.error("Escribe una nota");
    try { await api.post(`/quote-requests/${cur.id}/note`, { nota }); setNota(""); await load(); toast.success("Nota agregada"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const onUpload = async (e) => {
    const sel = e.target.files?.[0]; if (!sel) return;
    if (sel.size > 10 * 1024 * 1024) { toast.error("Máx. 10 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", sel);
      await api.post(`/quote-requests/${cur.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load(); toast.success("Adjunto interno agregado");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const convert = async () => {
    try {
      const { data } = await api.post(`/quote-requests/${cur.id}/convert`, {});
      toast.success(data.created ? "Cliente creado. Abriendo cotización..." : "Cliente existente. Abriendo cotización...");
      nav(`/app/cotizaciones/nueva?cliente=${data.client_id}&qr=${cur.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="cotizaciones-web-page">
      <div>
        <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2"><Inbox className="w-6 h-6" /> Solicitudes WEB</h1>
        <p className="text-sm text-muted-foreground mt-1">Solicitudes recibidas desde el sitio público. Ábrelas para revisar a detalle, agregar notas y adjuntos internos, y convertirlas en cotización.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="cw-filters">
        {["todas", "nuevo", "en_proceso", "convertida", "descartada"].map((k) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`cw-filter-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === k ? "bg-primary text-primary-foreground border-primary" : "bg-white text-muted-foreground border-border hover:border-primary/40"}`}>
            {k === "todas" ? "Todas" : EST[k].label} {k !== "todas" && counts[k] ? `(${counts[k]})` : ""}
          </button>
        ))}
      </div>

      <Card className="p-5 rounded-lg">
        {view.length === 0
          ? <div className="text-sm text-muted-foreground py-10 text-center">No hay solicitudes en esta vista.</div>
          : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Fecha</TableHead><TableHead>Contacto</TableHead><TableHead>Requerimiento</TableHead>
              <TableHead>Estatus</TableHead><TableHead className="text-right">Acciones</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {view.map((r) => (
                <TableRow key={r.id} data-testid={`cw-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-sm">{fmtDate(r.created_at)}</TableCell>
                  <TableCell>
                    <div className="font-medium">{r.nombre}</div>
                    <div className="text-xs text-muted-foreground">{r.empresa}</div>
                    <div className="text-xs text-muted-foreground">{r.email}</div>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="text-xs text-muted-foreground">{[r.tipo, r.industria, r.pais].filter(Boolean).join(" · ")}</div>
                    <div className="text-sm line-clamp-1">{r.descripcion || <span className="text-muted-foreground">—</span>}</div>
                    <div className="flex items-center gap-2 mt-1">
                      {r.file_id && <span className="text-[11px] text-interactive inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> adjunto</span>}
                      {r.internal_files?.length > 0 && <span className="text-[11px] text-muted-foreground">· {r.internal_files.length} interno(s)</span>}
                      {r.quotation_folio && <span className="text-[11px] text-emerald-700 font-mono inline-flex items-center gap-1"><FileText className="w-3 h-3" /> {r.quotation_folio}</span>}
                      {presenceMap[r.id]?.length > 0 && <PresenceChip viewers={presenceMap[r.id]} testid={`cw-presence-${r.id}`} />}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className={EST[r.estatus]?.cls}>{EST[r.estatus]?.label}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" className="h-8" onClick={() => openDetail(r)} data-testid={`cw-open-${r.id}`}><Eye className="w-3.5 h-3.5 mr-1" /> Ver detalle</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cw-detail">
          {cur && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 flex-wrap">
                  {cur.nombre} <Badge variant="outline" className={EST[cur.estatus]?.cls}>{EST[cur.estatus]?.label}</Badge>
                  <span className="text-xs font-normal text-muted-foreground">{fmtDateTime(cur.created_at)}</span>
                </DialogTitle>
              </DialogHeader>

              {detailViewers.length > 0 && <PresenceIndicator viewers={detailViewers} label="Revisando ahora" testid="cw-presence-banner" />}
              {cur.quotation_folio && (
                <div className="flex items-center justify-between gap-3 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm" data-testid="cw-linked-folio">
                  <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Convertida a cotización <b className="font-mono">{cur.quotation_folio}</b></span>
                  {cur.quotation_id && <Button size="sm" variant="outline" className="h-8" onClick={() => nav(`/app/cotizaciones/${cur.quotation_id}`)} data-testid="cw-goto-quote">Abrir <ExternalLink className="w-3.5 h-3.5 ml-1" /></Button>}
                </div>
              )}

              {/* General info */}
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {[["Empresa", cur.empresa], ["Correo", cur.email], ["Teléfono", cur.telefono], ["País", cur.pais], ["Industria", cur.industria], ["Tipo", cur.tipo]].map(([k, v]) => (
                  <div key={k}><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</div><div>{v || "—"}</div></div>
                ))}
                <div className="sm:col-span-2"><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Descripción</div><div className="whitespace-pre-wrap">{cur.descripcion || "—"}</div></div>
              </div>

              {/* Estatus */}
              <div className="flex items-center gap-3">
                <Label className="shrink-0">Estatus</Label>
                <Select value={cur.estatus} onValueChange={setEstatus}>
                  <SelectTrigger className="h-9 w-48" data-testid="cw-estatus"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(EST).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {/* Attachments */}
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Adjuntos</div>
                <div className="space-y-2">
                  {cur.file_id && (
                    <a href={`${BACKEND}/api/files/${cur.file_id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-interactive hover:underline" data-testid="cw-web-file">
                      <Paperclip className="w-4 h-4" /> {cur.file_name || "Archivo del cliente"} <span className="text-xs text-muted-foreground">(del cliente)</span>
                    </a>
                  )}
                  {(cur.internal_files || []).map((a) => (
                    <a key={a.file_id} href={`${BACKEND}/api/files/${a.file_id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-slate-700 hover:underline" data-testid={`cw-int-file-${a.file_id}`}>
                      <FileText className="w-4 h-4 text-muted-foreground" /> {a.file_name} <span className="text-xs text-muted-foreground">· {a.por} · {fmtDate(a.fecha)}</span>
                    </a>
                  ))}
                  {!cur.file_id && !(cur.internal_files || []).length && <div className="text-sm text-muted-foreground">Sin adjuntos.</div>}
                </div>
                <input ref={fileRef} type="file" className="hidden" onChange={onUpload} data-testid="cw-upload-input" accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.png,.jpg,.jpeg,.zip" />
                <Button size="sm" variant="outline" className="mt-3 h-8" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="cw-upload-btn">
                  <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? "Subiendo..." : "Adjuntar para revisión interna"}
                </Button>
              </div>

              {/* Internal notes */}
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Notas internas</div>
                <div className="flex gap-2">
                  <Textarea rows={2} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Agrega una nota de seguimiento interno..." data-testid="cw-note-text" />
                  <Button onClick={submitNote} className="self-end" data-testid="cw-note-submit">Agregar</Button>
                </div>
                {(cur.notas || []).length > 0 && (
                  <div className="mt-3 border rounded-md divide-y max-h-44 overflow-auto" data-testid="cw-note-history">
                    {[...cur.notas].reverse().map((s) => (
                      <div key={s.id} className="px-3 py-2 text-sm">
                        <div className="text-xs text-muted-foreground">{fmtDateTime(s.fecha)} · {s.por}</div>
                        <div>{s.nota}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Convert */}
              {!cur.quotation_folio && (
                <div className="pt-2 border-t">
                  <Button className="w-full" onClick={convert} data-testid="cw-convert"><ArrowRightLeft className="w-4 h-4 mr-2" /> Convertir a cotización</Button>
                  <p className="text-xs text-muted-foreground mt-2 text-center">Se creará (o vinculará) el cliente y se abrirá el editor de cotización. El folio quedará vinculado aquí al guardarla.</p>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
